
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Client } from '@/types';
import { AccessContextType, AccessInfo } from './types';
import { 
  fetchClients as fetchClientsOperation,
  createClient as createClientOperation,
  updateClient as updateClientOperation,
  deleteClient as deleteClientOperation
} from './clientOperations';
import {
  verifyAccessCode as verifyAccessCodeOperation,
  createAccessCode as createAccessCodeOperation
} from './accessCodeOperations';

const AccessContext = createContext<AccessContextType | undefined>(undefined);

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [clients, setClients] = useState<Client[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check for stored access info on component mount
  useEffect(() => {
    const storedAccess = localStorage.getItem('webflow_release_access');
    if (storedAccess) {
      try {
        const parsedAccess = JSON.parse(storedAccess) as AccessInfo;
        setAccessInfo(parsedAccess);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Failed to parse stored access info:', error);
        localStorage.removeItem('webflow_release_access');
      }
    }
    setIsLoading(false);
  }, []);

  const fetchClients = async (): Promise<void> => {
    const clientsList = await fetchClientsOperation(toast);
    setClients(clientsList);
  };

  useEffect(() => {
    if (isLoggedIn && accessInfo?.isAdmin) {
      fetchClients();
    }
  }, [isLoggedIn, accessInfo?.isAdmin]);

  const verifyAccessCode = async (code: string): Promise<boolean> => {
    const [success, newAccessInfo] = await verifyAccessCodeOperation(
      code, 
      toast, 
      accessInfo?.isAdmin ? fetchClients : undefined
    );
    
    if (success && newAccessInfo) {
      // Store access info in localStorage
      localStorage.setItem('webflow_release_access', JSON.stringify(newAccessInfo));
      setAccessInfo(newAccessInfo);
      setIsLoggedIn(true);
      return true;
    }
    
    return false;
  };

  const logout = () => {
    localStorage.removeItem('webflow_release_access');
    setAccessInfo(null);
    setIsLoggedIn(false);
    navigate('/');
  };

  const createClient = async (name: string): Promise<string | null> => {
    return createClientOperation(name, toast, fetchClients);
  };

  const updateClient = async (id: string, name: string): Promise<boolean> => {
    return updateClientOperation(id, name, toast, fetchClients);
  };

  const deleteClient = async (id: string): Promise<boolean> => {
    return deleteClientOperation(id, toast, fetchClients);
  };

  const createAccessCode = async (clientId: string | null, isAdmin: boolean): Promise<string | null> => {
    return createAccessCodeOperation(clientId, isAdmin, toast);
  };

  const contextValue: AccessContextType = {
    isLoggedIn,
    isAdmin: accessInfo?.isAdmin || false,
    clientId: accessInfo?.clientId || null,
    clientName: accessInfo?.clientName || null,
    userId: accessInfo?.userId || null, // Add userId to context value
    clients,
    verifyAccessCode,
    logout,
    isLoading,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    createAccessCode,
  };

  return (
    <AccessContext.Provider value={contextValue}>
      {children}
    </AccessContext.Provider>
  );
};

export const useAccessControl = () => {
  const context = useContext(AccessContext);
  if (context === undefined) {
    throw new Error('useAccessControl must be used within an AccessProvider');
  }
  return context;
};
